
import React, { useCallback, useEffect, useState } from "react";

const API_BASE = (
  process.env.REACT_APP_API_URL || "https://bmtvendorvendorfinal.onrender.com"
).replace(/\/$/, "");

const INITIAL_FORM = {
  title: "",
  description: "",
  code: "",
  discountType: "percentage",
  discountValue: "",
  minimumBookingAmount: "0",
  maximumDiscount: "",
  vertical: "all",
  startDate: "",
  endDate: "",
  usageLimit: "",
};

const VERTICALS = [
  ["all", "All Services"],
  ["hotel", "Hotel"],
  ["homestay", "Homestay"],
  ["resort", "Resort"],
  ["villa", "Villa"],
  ["apartment", "Apartment"],
  ["cab", "Cab"],
  ["bus", "Bus"],
  ["tour", "Tours"],
  ["activities", "Activities"],
  ["holiday", "Holiday Packages"],
  ["darshan", "Darshan"],
  ["cruise", "Cruise"],
  ["nightclub", "Nightclub"],
  ["palace", "Palace"],
  ["campsite", "Campsite"],
  ["bnb", "B&B"],
  ["vacation-house", "Vacation House"],
  ["motel", "Motel"],
  ["flight", "Flight"],
];

const STATUSES = [
  "all",
  "pending",
  "active",
  "paused",
  "rejected",
  "expired",
];

export default function VendorOffers() {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);
  const [statusFilter, setStatusFilter] = useState("all");
  const [verticalFilter, setVerticalFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [notice, setNotice] = useState({
    type: "",
    message: "",
  });

  const request = useCallback(async (path, options = {}) => {
    const token = localStorage.getItem("vendorToken");

    if (!token) {
      throw new Error("Vendor login token nahi mila. Please login again.");
    }

    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.success === false) {
      throw new Error(data.message || `Request failed (${response.status})`);
    }

    return data;
  }, []);

  const loadOffers = useCallback(async () => {
    setLoading(true);

    try {
      const params = new URLSearchParams();

      if (statusFilter !== "all") {
        params.set("status", statusFilter);
      }

      if (verticalFilter !== "all") {
        params.set("vertical", verticalFilter);
      }

      const query = params.toString();
      const data = await request(
        `/api/vendor/offers${query ? `?${query}` : ""}`
      );

      setOffers(Array.isArray(data.offers) ? data.offers : []);
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setLoading(false);
    }
  }, [request, statusFilter, verticalFilter]);

  useEffect(() => {
    loadOffers();
  }, [loadOffers]);

  const updateField = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm(INITIAL_FORM);
    setEditingId("");
    setShowForm(false);
  };

  const startEdit = (offer) => {
    const toLocalInput = (date) => {
      if (!date) return "";
      const parsed = new Date(date);
      const offset = parsed.getTimezoneOffset() * 60000;
      return new Date(parsed.getTime() - offset)
        .toISOString()
        .slice(0, 16);
    };

    setForm({
      title: offer.title || "",
      description: offer.description || "",
      code: offer.code || "",
      discountType: offer.discountType || "percentage",
      discountValue: String(offer.discountValue ?? ""),
      minimumBookingAmount: String(offer.minimumBookingAmount ?? 0),
      maximumDiscount:
        offer.maximumDiscount == null
          ? ""
          : String(offer.maximumDiscount),
      vertical: offer.vertical || "all",
      startDate: toLocalInput(offer.startDate),
      endDate: toLocalInput(offer.endDate),
      usageLimit:
        offer.usageLimit == null ? "" : String(offer.usageLimit),
    });

    setEditingId(offer._id);
    setShowForm(true);
    setNotice({ type: "", message: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submitOffer = async (event) => {
    event.preventDefault();
    setSaving(true);
    setNotice({ type: "", message: "" });

    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        discountValue: Number(form.discountValue),
        minimumBookingAmount: Number(form.minimumBookingAmount || 0),
        maximumDiscount:
          form.discountType === "percentage" && form.maximumDiscount !== ""
            ? Number(form.maximumDiscount)
            : null,
        usageLimit:
          form.usageLimit !== "" ? Number(form.usageLimit) : null,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
      };

      const data = await request(
        editingId
          ? `/api/vendor/offers/${editingId}`
          : "/api/vendor/offers",
        {
          method: editingId ? "PUT" : "POST",
          body: JSON.stringify(payload),
        }
      );

      setNotice({
        type: "success",
        message: data.message || "Offer saved successfully",
      });

      resetForm();
      await loadOffers();
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (offer) => {
    const action = offer.status === "active" ? "pause" : "resume";

    if (!window.confirm(`Are you sure you want to ${action} this offer?`)) {
      return;
    }

    setBusyId(offer._id);
    setNotice({ type: "", message: "" });

    try {
      const data = await request(
        `/api/vendor/offers/${offer._id}/toggle-status`,
        { method: "PATCH" }
      );

      setNotice({
        type: "success",
        message: data.message || "Offer status updated",
      });

      await loadOffers();
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setBusyId("");
    }
  };

  const deleteOffer = async (offer) => {
    if (!window.confirm(`Delete offer "${offer.title}"?`)) return;

    setBusyId(offer._id);
    setNotice({ type: "", message: "" });

    try {
      const data = await request(`/api/vendor/offers/${offer._id}`, {
        method: "DELETE",
      });

      setNotice({
        type: "success",
        message: data.message || "Offer deleted",
      });

      await loadOffers();
    } catch (error) {
      setNotice({ type: "error", message: error.message });
    } finally {
      setBusyId("");
    }
  };

  const counts = {
    total: offers.length,
    pending: offers.filter((offer) => offer.status === "pending").length,
    active: offers.filter((offer) => offer.status === "active").length,
    paused: offers.filter((offer) => offer.status === "paused").length,
  };

  const money = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(value || 0));

  const badgeClass = (status) =>
    `vo-status vo-status-${status || "pending"}`;

  return (
    <div className="vo-page">
      <style>{`
        .vo-page {
          color: #172033;
          font-family: Inter, Arial, sans-serif;
          padding: 24px;
          background: #f7f8fc;
          min-height: 100%;
        }
        .vo-top {
          display:flex;justify-content:space-between;align-items:center;
          gap:16px;flex-wrap:wrap;margin-bottom:22px;
        }
        .vo-title { margin:0;font-size:26px;font-weight:750; }
        .vo-subtitle { color:#687386;margin:7px 0 0;font-size:14px; }
        .vo-btn {
          border:0;border-radius:9px;padding:11px 16px;
          cursor:pointer;font-weight:650;font-size:13px;
        }
        .vo-primary { background:#1c4ed8;color:white; }
        .vo-secondary { background:#eef2ff;color:#253e91; }
        .vo-danger { background:#fff0f0;color:#b42318; }
        .vo-btn:disabled { opacity:.55;cursor:not-allowed; }
        .vo-stats {
          display:grid;grid-template-columns:repeat(4,minmax(0,1fr));
          gap:14px;margin-bottom:22px;
        }
        .vo-stat,.vo-panel {
          background:white;border:1px solid #e7eaf1;
          border-radius:13px;padding:18px;
        }
        .vo-stat-label { color:#687386;font-size:13px; }
        .vo-stat-value { font-size:25px;font-weight:750;margin-top:9px; }
        .vo-filters {
          display:flex;gap:12px;flex-wrap:wrap;align-items:center;
          margin-bottom:18px;
        }
        .vo-input,.vo-select,.vo-textarea {
          box-sizing:border-box;width:100%;border:1px solid #d9dfeb;
          border-radius:8px;padding:11px 12px;font:inherit;background:white;
          color:#172033;min-width:0;
        }
        .vo-filter { width:190px;max-width:100%; }
        .vo-grid {
          display:grid;grid-template-columns:repeat(2,minmax(0,1fr));
          gap:14px;
        }
        .vo-form-grid {
          display:grid;grid-template-columns:repeat(2,minmax(0,1fr));
          gap:15px;
        }
        .vo-field { display:flex;flex-direction:column;gap:7px; }
        .vo-field label { font-size:13px;font-weight:650;color:#344054; }
        .vo-full { grid-column:1/-1; }
        .vo-card-head {
          display:flex;justify-content:space-between;gap:12px;align-items:flex-start;
        }
        .vo-card-title { margin:0;font-size:17px;font-weight:750; }
        .vo-code {
          display:inline-block;margin-top:9px;padding:6px 9px;
          background:#f0f4ff;border-radius:6px;color:#2548a5;
          font-size:12px;font-weight:750;letter-spacing:.5px;
        }
        .vo-status {
          display:inline-block;padding:5px 9px;border-radius:20px;
          font-size:11px;font-weight:750;text-transform:capitalize;
          white-space:nowrap;
        }
        .vo-status-pending { background:#fff4d6;color:#946200; }
        .vo-status-active { background:#dff8e8;color:#147342; }
        .vo-status-paused { background:#e9edf5;color:#526078; }
        .vo-status-rejected { background:#ffe5e5;color:#b42318; }
        .vo-status-expired { background:#f0e9ff;color:#6941c6; }
        .vo-details {
          display:grid;grid-template-columns:repeat(2,minmax(0,1fr));
          gap:12px;margin:18px 0;
        }
        .vo-detail-label { font-size:12px;color:#778196; }
        .vo-detail-value { font-size:14px;font-weight:700;margin-top:4px; }
        .vo-actions { display:flex;gap:8px;flex-wrap:wrap;margin-top:14px; }
        .vo-notice { margin-bottom:16px;padding:12px 14px;border-radius:9px;font-size:14px; }
        .vo-notice-success { background:#e7f8ed;color:#17663a; }
        .vo-notice-error { background:#fff0f0;color:#a51d1d; }
        .vo-muted { color:#778196;font-size:13px; }
        .vo-empty { padding:38px 15px;text-align:center;color:#687386; }
        @media(max-width:900px) {
          .vo-stats { grid-template-columns:repeat(2,minmax(0,1fr)); }
          .vo-grid { grid-template-columns:1fr; }
        }
        @media(max-width:600px) {
          .vo-page { padding:14px; }
          .vo-title { font-size:22px; }
          .vo-form-grid { grid-template-columns:1fr; }
          .vo-full { grid-column:auto; }
          .vo-stats { gap:9px; }
          .vo-stat { padding:13px; }
          .vo-stat-value { font-size:21px; }
          .vo-details { gap:10px; }
        }
      `}</style>

      <div className="vo-top">
        <div>
          <h1 className="vo-title">Offers & Promotions</h1>
          <p className="vo-subtitle">
            Create coupon offers and manage promotions for your services.
          </p>
        </div>

        <button
          className="vo-btn vo-primary"
          onClick={() => {
            if (showForm && !editingId) {
              resetForm();
            } else {
              setForm(INITIAL_FORM);
              setEditingId("");
              setShowForm(true);
            }
          }}
        >
          {showForm && !editingId ? "Close Form" : "+ Create Offer"}
        </button>
      </div>

      {notice.message && (
        <div className={`vo-notice vo-notice-${notice.type}`}>
          {notice.message}
        </div>
      )}

      <div className="vo-stats">
        <div className="vo-stat">
          <div className="vo-stat-label">Offers Shown</div>
          <div className="vo-stat-value">{counts.total}</div>
        </div>
        <div className="vo-stat">
          <div className="vo-stat-label">Pending Approval</div>
          <div className="vo-stat-value">{counts.pending}</div>
        </div>
        <div className="vo-stat">
          <div className="vo-stat-label">Active Offers</div>
          <div className="vo-stat-value">{counts.active}</div>
        </div>
        <div className="vo-stat">
          <div className="vo-stat-label">Paused Offers</div>
          <div className="vo-stat-value">{counts.paused}</div>
        </div>
      </div>

      {showForm && (
        <section className="vo-panel" style={{ marginBottom: 22 }}>
          <h2 style={{ marginTop: 0, fontSize: 19 }}>
            {editingId ? "Edit Offer" : "Create New Offer"}
          </h2>
          <p className="vo-muted">
            New and edited offers are submitted for admin approval.
          </p>

          <form onSubmit={submitOffer}>
            <div className="vo-form-grid">
              <div className="vo-field">
                <label htmlFor="vo-title">Offer Title *</label>
                <input
                  id="vo-title"
                  className="vo-input"
                  name="title"
                  value={form.title}
                  onChange={updateField}
                  maxLength={120}
                  required
                  placeholder="e.g. Festive Special"
                />
              </div>

              <div className="vo-field">
                <label htmlFor="vo-code">Coupon Code *</label>
                <input
                  id="vo-code"
                  className="vo-input"
                  name="code"
                  value={form.code}
                  onChange={updateField}
                  minLength={3}
                  maxLength={30}
                  required
                  placeholder="e.g. FESTIVE20"
                  style={{ textTransform: "uppercase" }}
                />
              </div>

              <div className="vo-field vo-full">
                <label htmlFor="vo-description">Description</label>
                <textarea
                  id="vo-description"
                  className="vo-textarea"
                  name="description"
                  value={form.description}
                  onChange={updateField}
                  maxLength={1000}
                  rows={3}
                  placeholder="Describe your offer..."
                />
              </div>

              <div className="vo-field">
                <label htmlFor="vo-discount-type">Discount Type *</label>
                <select
                  id="vo-discount-type"
                  className="vo-select"
                  name="discountType"
                  value={form.discountType}
                  onChange={updateField}
                  required
                >
                  <option value="percentage">Percentage (%)</option>
                  <option value="fixed">Fixed Amount (₹)</option>
                </select>
              </div>

              <div className="vo-field">
                <label htmlFor="vo-discount-value">Discount Value *</label>
                <input
                  id="vo-discount-value"
                  className="vo-input"
                  name="discountValue"
                  type="number"
                  min="0.01"
                  max={form.discountType === "percentage" ? 100 : undefined}
                  step="0.01"
                  value={form.discountValue}
                  onChange={updateField}
                  required
                />
              </div>

              <div className="vo-field">
                <label htmlFor="vo-minimum">Minimum Booking Amount (₹)</label>
                <input
                  id="vo-minimum"
                  className="vo-input"
                  name="minimumBookingAmount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.minimumBookingAmount}
                  onChange={updateField}
                />
              </div>

              {form.discountType === "percentage" && (
                <div className="vo-field">
                  <label htmlFor="vo-max-discount">
                    Maximum Discount (₹), optional
                  </label>
                  <input
                    id="vo-max-discount"
                    className="vo-input"
                    name="maximumDiscount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.maximumDiscount}
                    onChange={updateField}
                    placeholder="No cap"
                  />
                </div>
              )}

              <div className="vo-field">
                <label htmlFor="vo-vertical">Apply To *</label>
                <select
                  id="vo-vertical"
                  className="vo-select"
                  name="vertical"
                  value={form.vertical}
                  onChange={updateField}
                  required
                >
                  {VERTICALS.map(([value, label]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              <div className="vo-field">
                <label htmlFor="vo-usage-limit">Usage Limit, optional</label>
                <input
                  id="vo-usage-limit"
                  className="vo-input"
                  name="usageLimit"
                  type="number"
                  min="1"
                  step="1"
                  value={form.usageLimit}
                  onChange={updateField}
                  placeholder="Unlimited"
                />
              </div>

              <div className="vo-field">
                <label htmlFor="vo-start-date">Start Date & Time *</label>
                <input
                  id="vo-start-date"
                  className="vo-input"
                  name="startDate"
                  type="datetime-local"
                  value={form.startDate}
                  onChange={updateField}
                  required
                />
              </div>

              <div className="vo-field">
                <label htmlFor="vo-end-date">End Date & Time *</label>
                <input
                  id="vo-end-date"
                  className="vo-input"
                  name="endDate"
                  type="datetime-local"
                  value={form.endDate}
                  onChange={updateField}
                  min={form.startDate || undefined}
                  required
                />
              </div>
            </div>

            <div className="vo-actions">
              <button className="vo-btn vo-primary" disabled={saving}>
                {saving ? "Saving..." : editingId ? "Update Offer" : "Submit Offer"}
              </button>
              <button
                type="button"
                className="vo-btn vo-secondary"
                onClick={resetForm}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </form>
        </section>
      )}

      <div className="vo-filters">
        <select
          className="vo-select vo-filter"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          aria-label="Filter by status"
        >
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {status === "all"
                ? "All Statuses"
                : status.charAt(0).toUpperCase() + status.slice(1)}
            </option>
          ))}
        </select>

        <select
          className="vo-select vo-filter"
          value={verticalFilter}
          onChange={(event) => setVerticalFilter(event.target.value)}
          aria-label="Filter by service"
        >
          <option value="all">All Services</option>
          {VERTICALS.filter(([value]) => value !== "all").map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <button
          className="vo-btn vo-secondary"
          onClick={loadOffers}
          disabled={loading}
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="vo-panel vo-empty">Loading offers...</div>
      ) : offers.length === 0 ? (
        <div className="vo-panel vo-empty">
          <h3 style={{ marginTop: 0, color: "#344054" }}>No offers found</h3>
          <p>Create your first offer or change the filters.</p>
        </div>
      ) : (
        <div className="vo-grid">
          {offers.map((offer) => (
            <article className="vo-panel" key={offer._id}>
              <div className="vo-card-head">
                <div>
                  <h3 className="vo-card-title">{offer.title}</h3>
                  <span className="vo-code">{offer.code}</span>
                </div>
                <span className={badgeClass(offer.status)}>
                  {offer.status}
                </span>
              </div>

              {offer.description && (
                <p className="vo-muted" style={{ lineHeight: 1.5 }}>
                  {offer.description}
                </p>
              )}

              <div className="vo-details">
                <div>
                  <div className="vo-detail-label">Discount</div>
                  <div className="vo-detail-value">
                    {offer.discountType === "percentage"
                      ? `${offer.discountValue}%`
                      : money(offer.discountValue)}
                  </div>
                </div>
                <div>
                  <div className="vo-detail-label">Minimum Booking</div>
                  <div className="vo-detail-value">
                    {money(offer.minimumBookingAmount)}
                  </div>
                </div>
                <div>
                  <div className="vo-detail-label">Service</div>
                  <div className="vo-detail-value">
                    {VERTICALS.find(([value]) => value === offer.vertical)?.[1]
                      || offer.vertical}
                  </div>
                </div>
                <div>
                  <div className="vo-detail-label">Usage</div>
                  <div className="vo-detail-value">
                    {offer.usedCount || 0} / {offer.usageLimit ?? "Unlimited"}
                  </div>
                </div>
                <div>
                  <div className="vo-detail-label">Starts</div>
                  <div className="vo-detail-value">
                    {offer.startDate
                      ? new Date(offer.startDate).toLocaleString("en-IN")
                      : "—"}
                  </div>
                </div>
                <div>
                  <div className="vo-detail-label">Ends</div>
                  <div className="vo-detail-value">
                    {offer.endDate
                      ? new Date(offer.endDate).toLocaleString("en-IN")
                      : "—"}
                  </div>
                </div>
              </div>

              {offer.rejectionReason && (
                <p style={{ color: "#b42318", fontSize: 13 }}>
                  Reason: {offer.rejectionReason}
                </p>
              )}

              <div className="vo-actions">
                {["pending", "rejected"].includes(offer.status) && (
                  <button
                    className="vo-btn vo-secondary"
                    onClick={() => startEdit(offer)}
                  >
                    Edit
                  </button>
                )}

                {["active", "paused"].includes(offer.status) && (
                  <button
                    className="vo-btn vo-secondary"
                    onClick={() => toggleStatus(offer)}
                    disabled={busyId === offer._id}
                  >
                    {busyId === offer._id
                      ? "Please wait..."
                      : offer.status === "active"
                      ? "Pause"
                      : "Resume"}
                  </button>
                )}

                {offer.status !== "active" && (
                  <button
                    className="vo-btn vo-danger"
                    onClick={() => deleteOffer(offer)}
                    disabled={busyId === offer._id}
                  >
                    Delete
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
